// Hideo - theme (light / dark / automatic) and main colour: the colour maths without UI.
// Ported from the legacy core/theme.js (verbatim copy in tests/fixtures/legacy/theme.js, golden tests in tests/theme.test.ts).
// The theme and the colour are applied to the page by src/stores/theme.ts; the anti-flash inline script in index.html's
// <head> repeats accentVars in a few lines (tests/theme.test.ts checks that it gives the same values).
// Stored on this device: creatorOS_theme = 'light' | 'dark' | 'auto' (missing = dark), creatorOS_accent = #rrggbb.
import { contrastOf, isHex } from '../utils/color'

export const THEME_KEY = 'creatorOS_theme'
export const ACCENT_KEY = 'creatorOS_accent'
/** Default main colour */
export const DEF_ACCENT = '#e85d4e'
/** The quick colours of Settings › Appearance › Main colour (lower case): Hideo's red, then the colours of the old
 *  coloured themes (violet, blue, green, amber, turquoise, Fluent's light blue, light grey) */
export const ACCENT_PRESETS: readonly string[] = [DEF_ACCENT, '#8b5cf6', '#4a86e0', '#4caf7d', '#e0902f', '#22c9a0', '#60cdff', '#d8d8dc']
/** Readability (WCAG contrast): 3 is the minimum for bold text and icons */
const ON_BRAND_MIN = 3
const TEXT_MIN = 3
export type Rgb = number[]
/** Backgrounds of each theme (--bg, --card, --inp): a colour used as text must be readable on all of them */
const THEME_BGS: Record<'dark' | 'light', Rgb[]> = {
  dark: [[15, 15, 15], [26, 26, 26], [38, 38, 38]],
  light: [[244, 244, 242], [255, 255, 255], [243, 242, 240]]
}

/** The theme shown for a preference: 'auto' follows the system (dark unless the system is light) */
export const resolveTheme = (m: string, systemDark: boolean | null): string => (m === 'auto' ? (systemDark === false ? 'light' : 'dark') : m)

/** "#rrggbb" → [r, g, b] (no check: see validHex) */
export const hex2rgb = (h: string): Rgb => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16))
/** [r, g, b] → "#rrggbb" */
export const toHex = (c: Rgb): string => '#' + c.map(v => v.toString(16).padStart(2, '0')).join('')
/** a mixed with b: k = 0 → a, k = 1 → b (rounded) */
export const mixc = (a: Rgb, b: Rgb, k: number): Rgb => a.map((v, i) => Math.round(v * (1 - k) + b[i] * k))
/** Colour c with its hue turned by deg degrees (same lightness and saturation): the halos of Blurred bubbles
 *  (src/features/sky.ts) take the main colour and two neighbours */
export function shiftHue(c: Rgb, deg: number): Rgb {
  const [r, g, b] = c.map(v => v / 255), max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2, d = max - min
  if (!d) return [...c]
  const s = d / (1 - Math.abs(2 * l - 1))
  let h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4
  h = ((h * 60 + deg) % 360 + 360) % 360
  const k = (1 - Math.abs(2 * l - 1)) * s, x = k * (1 - Math.abs((h / 60) % 2 - 1)), m = l - k / 2
  const [a, e, f] = h < 60 ? [k, x, 0] : h < 120 ? [x, k, 0] : h < 180 ? [0, k, x] : h < 240 ? [0, x, k] : h < 300 ? [x, 0, k] : [k, 0, x]
  return [a, e, f].map(v => Math.max(0, Math.min(255, Math.round((v + m) * 255))))
}
/** A #rrggbb colour (isHex, src/utils/color.ts) */
export const validHex = isHex

/** Colour c, moved towards white (dark theme) or black (light theme) just enough to be readable on all the backgrounds bgs */
export function readableOn(c: Rgb, bgs: Rgb[], dark: boolean): Rgb {
  const ok = (x: Rgb) => bgs.every(b => contrastOf(x, b) >= TEXT_MIN)
  for (let k = 0; k <= 1.001; k += 0.05) { const x = mixc(c, dark ? [255, 255, 255] : [0, 0, 0], k); if (ok(x)) return x }
  return dark ? [255, 255, 255] : [0, 0, 0]
}

/** A tag or format colour (#rrggbb) readable as text on a theme's background; anything else is returned unchanged */
export const readableHexOn = (hex: string, dark: boolean): string =>
  (validHex(hex) ? toHex(readableOn(hex2rgb(hex), THEME_BGS[dark ? 'dark' : 'light'], dark)) : hex)

/** The theme shown now is dark (<html class="dark">) */
export const isDarkTheme = (): boolean => typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
/** A tag or format colour made readable as text on the current theme's background */
export const readableHex = (hex: string): string => readableHexOn(hex, isDarkTheme())
/** Text colour to use on a #rrggbb background: white, or near-black when white is unreadable (#ffffff for anything else) */
export const onHex = (hex: string): string => (validHex(hex) && contrastOf([255, 255, 255], hex2rgb(hex)) < ON_BRAND_MIN ? '#111111' : '#ffffff')

/** The backgrounds a theme's colours are mixed with and must be readable on (the app themes of
 *  src/features/appThemes.ts pass their own; without them, Hideo's light or dark theme) */
export interface AccentBase {
  /** --bg, --card, --inp of the theme: the main colour used as text must be readable on all of them */
  bgs: Rgb[]
  /** The colour the tinted backgrounds (--r950, --r900, --thumb) start from */
  base: Rgb
}

/** The CSS variables derived from the main colour, in the order they are set ("r g b" values) */
export function accentVars(hex: string, dark: boolean, on?: AccentBase): [string, string][] {
  const c = hex2rgb(hex), base = on ? on.base : dark ? [0, 0, 0] : [255, 255, 255]
  return [
    ['--brand', c.join(' ')],
    ['--brand-dark', mixc(c, [0, 0, 0], 0.18).join(' ')],
    ['--r950', mixc(base, c, dark ? 0.28 : 0.12).join(' ')],
    ['--r900', mixc(base, c, dark ? 0.5 : 0.4).join(' ')],
    ['--thumb', mixc(on ? base : dark ? [26, 26, 26] : base, c, dark ? 0.14 : 0.1).join(' ')],
    // Text on the main colour (filled buttons): white, unless white is unreadable → black
    ['--on-brand', (contrastOf([255, 255, 255], c) >= ON_BRAND_MIN ? [255, 255, 255] : [17, 17, 17]).join(' ')],
    // Main colour used as text (links, icons, labels): darkened / lightened if it blends into the theme background
    ['--brand-text', readableOn(c, on ? on.bgs : THEME_BGS[dark ? 'dark' : 'light'], dark).join(' ')]
  ]
}
